// apps/creativity/src/screens/ResponseDetailScreen.jsx
import { useEffect, useRef, useState } from 'react';
import {
  collection, addDoc, onSnapshot,
  doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../firebase/useAuth';
import { useApp } from '../context/AppContext';
import { CATEGORIES } from '../data/categories';
import ReactionBar from '../components/ReactionBar';
import styles from './ResponseDetailScreen.module.css';

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

function Avatar({ photoURL, displayName }) {
  if (photoURL) {
    return <img src={photoURL} alt={displayName} className={styles.replyAvatar} />;
  }
  return (
    <div className={styles.replyAvatarFallback}>
      {(displayName || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function ResponseDetailScreen() {
  const { user } = useAuth();
  const { state, dispatch } = useApp();
  const { activeDetail } = state;
  const {
    ownerUserId, entryId, categoryKey,
    responseText, entryDate,
    ownerName, isOwn,
  } = activeDetail;

  const cat = CATEGORIES.find(c => c.key === categoryKey);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'users', ownerUserId, 'entries', entryId, 'replies'),
      (snap) => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const filtered = all
          .filter(r => r.categoryKey === categoryKey)
          .sort((a, b) => (a.at?.seconds || 0) - (b.at?.seconds || 0));
        setReplies(filtered);
      }
    );
    return () => unsub();
  }, [ownerUserId, entryId, categoryKey]);

  // Scroll to bottom when new replies arrive
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [replies]);

  // Clear hasUnread if this is the owner viewing their own entry
  useEffect(() => {
    if (!isOwn) return;
    const entryRef = doc(db, 'users', ownerUserId, 'entries', entryId);
    updateDoc(entryRef, {
      [`interactions.${categoryKey}.hasUnread`]: false,
    }).catch(() => {});
  }, [isOwn, ownerUserId, entryId, categoryKey]);

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || sending) return;
    setSending(true);
    setReplyText('');
    try {
      await addDoc(
        collection(db, 'users', ownerUserId, 'entries', entryId, 'replies'),
        {
          categoryKey,
          text,
          authorId: user.uid,
          authorName: user.displayName || user.email || 'Anonymous',
          authorPhotoURL: user.photoURL || null,
          at: serverTimestamp(),
        }
      );
    } catch (err) {
      console.warn('Reply send failed:', err);
      setReplyText(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => history.back()}>
          ←
        </button>
        <span className={styles.title}>
          {ownerName}'s {cat?.label} — {formatDate(entryDate)}
        </span>
      </header>

      <div className={styles.body} ref={threadRef}>
        <div
          className={styles.responseBlock}
          style={{ '--accent': `var(${cat?.accentVar})` }}
        >
          <span className={styles.catLabel}>{cat?.icon} {cat?.label}</span>
          <p className={styles.responseText}>{responseText}</p>
        </div>

        <ReactionBar
          ownerUserId={ownerUserId}
          entryId={entryId}
          categoryKey={categoryKey}
          accentVar={cat?.accentVar}
        />

        <div className={styles.thread}>
          {replies.length === 0 && (
            <p className={styles.emptyThread}>No replies yet. Be the first!</p>
          )}
          {replies.map(reply => {
            const isMine = reply.authorId === user.uid;
            const isOwner = reply.authorId === ownerUserId;
            return (
              <div
                key={reply.id}
                className={`${styles.replyItem} ${isMine ? styles.mine : ''}`}
              >
                {!isMine && (
                  <Avatar
                    photoURL={reply.authorPhotoURL}
                    displayName={reply.authorName}
                  />
                )}
                <div className={styles.replyContent}>
                  <div className={styles.replyMeta}>
                    {reply.authorName} · {formatTime(reply.at)}
                  </div>
                  <div
                    className={styles.replyBubble}
                    style={isOwner ? { '--accent': `var(${cat?.accentVar})` } : {}}
                    data-owner={isOwner ? 'true' : undefined}
                  >
                    {reply.text}
                  </div>
                </div>
                {isMine && (
                  <Avatar
                    photoURL={user.photoURL}
                    displayName={user.displayName || '?'}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.compose}>
        <textarea
          className={styles.composeInput}
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Reply…"
          rows={1}
        />
        <button
          className={styles.sendBtn}
          onClick={sendReply}
          disabled={!replyText.trim() || sending}
        >
          ↑
        </button>
      </div>
    </div>
  );
}
